import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Bus, User, Save, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function DriverBusSettings() {
  const [drivers, setDrivers] = useState([]);
  const [buses, setBuses] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [driversRes, busesRes, assignmentsRes] = await Promise.all([
        axios.get(`${API}/api/users`, getAuthHeaders()),
        axios.get(`${API}/api/buses`, getAuthHeaders()),
        axios.get(`${API}/api/driver-bus-assignments`, getAuthHeaders())
      ]);

      // Filter only drivers
      const driversList = driversRes.data.filter(u => u.is_driver || u.role === 'driver');
      setDrivers(driversList);
      setBuses(busesRes.data);

      // Convert assignments array to object { driver_id: bus_id }
      const assignmentsMap = {};
      assignmentsRes.data.forEach(a => {
        assignmentsMap[a.driver_id] = a.bus_id;
      });
      setAssignments(assignmentsMap);
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentChange = async (driverId, busId) => {
    setSaving(driverId);
    try {
      await axios.post(`${API}/api/driver-bus-assignments`, {
        driver_id: driverId,
        bus_id: busId || null
      }, getAuthHeaders());

      setAssignments(prev => ({
        ...prev,
        [driverId]: busId || null
      }));
      
      toast.success('Привязка сохранена');
    } catch (error) {
      toast.error('Ошибка сохранения');
    } finally {
      setSaving(null);
    }
  };

  const getDriverName = (driver) => {
    if (driver.first_name || driver.last_name) {
      return `${driver.first_name || ''} ${driver.last_name || ''}`.trim();
    }
    return driver.username;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Привязка водителей к автобусам</h2>
          <p className="text-sm text-gray-500 mt-1">
            Выберите автобус для каждого водителя. В планировщике автобус будет подставляться автоматически.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Обновить
        </Button>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Водитель
                </div>
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                <div className="flex items-center gap-2">
                  <Bus className="w-4 h-4" />
                  Привязанный автобус
                </div>
              </th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 w-20">
                Статус
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {drivers.map((driver, idx) => {
              const assignedBusId = assignments[driver.id];
              const assignedBus = buses.find(b => b.id === assignedBusId);
              const isSaving = saving === driver.id;

              return (
                <tr 
                  key={driver.id}
                  className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                        {getDriverName(driver).charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-gray-800">{getDriverName(driver)}</div>
                        <div className="text-xs text-gray-500">@{driver.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={assignedBusId || ''}
                      onChange={(e) => handleAssignmentChange(driver.id, e.target.value)}
                      disabled={isSaving}
                      className={`w-full max-w-xs px-3 py-2 border rounded-lg transition-colors ${
                        assignedBusId 
                          ? 'border-green-300 bg-green-50 text-green-800' 
                          : 'border-gray-300 bg-white text-gray-600'
                      } focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50`}
                    >
                      <option value="">— Не выбран —</option>
                      {buses.map(bus => (
                        <option key={bus.id} value={bus.id}>
                          {bus.license_plate} {bus.brand ? `(${bus.brand} ${bus.model || ''})` : ''}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {isSaving ? (
                      <RefreshCw className="w-5 h-5 animate-spin text-blue-500 mx-auto" />
                    ) : assignedBusId ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <Bus className="w-3 h-3" />
                        Привязан
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                        Нет
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {drivers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Водители не найдены</p>
          </div>
        )}
      </Card>

      <div className="text-sm text-gray-500 bg-blue-50 p-4 rounded-lg">
        <strong>Как это работает:</strong>
        <ul className="mt-2 space-y-1 list-disc list-inside">
          <li>Выберите автобус напротив каждого водителя</li>
          <li>В планировщике при выборе водителя автобус подставится автоматически</li>
          <li>При выборе автобуса - подставится привязанный водитель</li>
          <li>Вы можете изменить автобус для конкретного рейса — привязка в настройках не изменится</li>
        </ul>
      </div>
    </div>
  );
}
